import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragOverlay, DragEndEvent, DragStartEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import { ComposableMap, Geographies, Geography, ZoomableGroup, Marker } from 'react-simple-maps';
import { geoCentroid } from 'd3-geo';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './WorldCountriesQuiz.module.css';
import { Country, geoData, regionMap, regions } from './countryData';

type NamedGeography = {
  properties: { name: string };
  [key: string]: unknown;
};

type PlacementState = Record<string, boolean>;

type DraggableLabelProps = {
  country: Country;
  placed: boolean;
};

const DraggableLabel: React.FC<DraggableLabelProps> = ({ country, placed }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: country.id,
    disabled: placed,
  });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={[
        styles.pill,
        isDragging ? styles.pillDragging : '',
        placed ? styles.pillLocked : '',
        placed ? styles.pillPlaced : '',
      ].join(' ')}
      aria-disabled={placed}
    >
      {country.label}
      {country.code && <span className={styles.pillCode}>({country.code})</span>}
    </div>
  );
};

type CountryZoneProps = {
  country: Country;
  geography: NamedGeography;
  solved: boolean;
};

const CountryZone: React.FC<CountryZoneProps> = ({ country, geography, solved }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: country.id,
  });

  const fill = solved ? '#6FCF97' : isOver ? '#FFE082' : '#E8EDF5';

  return (
    <Geography
      ref={setNodeRef}
      geography={geography}
      fill={fill}
      stroke="#1C2F46"
      strokeWidth={0.6}
      style={{
        default: { outline: 'none' },
        hover: { outline: 'none', fill },
        pressed: { outline: 'none' },
      }}
    />
  );
};

const WorldCountriesQuiz: React.FC = () => {
  const [placed, setPlaced] = useState<PlacementState>({});
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [mapSize, setMapSize] = useState({ width: 900, height: 780 });
  const [zoom, setZoom] = useState(1);
  const [center, setCenter] = useState<[number, number]>([20, 5]);
  const [toast, setToast] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);
  const [showComplete, setShowComplete] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [regionId, setRegionId] = useState<string>('africa');
  const [resetKey, setResetKey] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateSize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      if (viewportWidth <= 1100) {
        const targetWidth = viewportWidth - 32;
        const proportionalHeight = Math.round(targetWidth * 1.05);
        const availableHeight = Math.max(520, viewportHeight - 220);
        const height = Math.min(availableHeight, proportionalHeight);
        setMapSize({ width: targetWidth, height });
        return;
      }
      const targetWidth = Math.min(1200, viewportWidth - 200);
      const proportionalHeight = Math.round(targetWidth * 0.95);
      const availableHeight = Math.max(780, viewportHeight - 200);
      const height = Math.min(availableHeight, proportionalHeight);
      setMapSize({ width: targetWidth, height });
    };

    updateSize();
    window.addEventListener('resize', updateSize);

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
    };
  }, []);

  const region = useMemo(() => regionMap.get(regionId) ?? regions[0], [regionId]);

  const countryByMapName = useMemo(() => {
    const lookup = new Map<string, Country>();
    region.countries.forEach((c) => lookup.set(c.mapName, c));
    return lookup;
  }, [region]);

  const mapScale = useMemo(() => {
    const multiplier =
      mapSize.width <= 900 ? 0.8 : region.scaleMultiplier ?? (region.center ? 0.78 : 0.75);
    const maxScale =
      region.maxScale ??
      (mapSize.width <= 900 ? 520 : region.center ? 720 : 700);
    return Math.min(maxScale, mapSize.width * multiplier);
  }, [mapSize.width, region]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 1500);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  useEffect(() => {
    setPlaced({});
    setScore({ correct: 0, wrong: 0 });
    setZoom(1);
    setCenter(region.center ?? [20, 5]);
    setShowComplete(false);
    setToast(null);
  }, [region, resetKey]);

  useEffect(() => {
    if (region.countries.length > 0 && Object.keys(placed).length === region.countries.length) {
      setShowComplete(true);
    }
  }, [placed, region]);

  const handleZoomChange = (delta: number) => {
    setZoom((prev) => {
      const next = Math.max(1, Math.min(4, prev + delta));
      return next;
    });
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId === overId) {
      const matched = region.countries.find((c) => c.id === activeId);
      setPlaced((prev) => ({ ...prev, [activeId]: true }));
      setScore((prev) => ({ ...prev, correct: prev.correct + 1 }));
      const code = matched?.code ? ` (${matched.code})` : '';
      setToast({ message: `Nice! Correct location of ${matched?.label ?? 'country'}${code}! 👏`, kind: 'success' });
    } else {
      setScore((prev) => ({ ...prev, wrong: prev.wrong + 1 }));
      setToast({ message: 'Not quite—try again 🙂', kind: 'error' });
    }
  };

  return (
    <div className={styles.page}>
      <TopNavBar />
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className={styles.content}>
          <div className={styles.mapPanel}>
            <h1 className={styles.mapTitle}>Do You Know {region.label}?</h1>
            <p className={styles.subtitle}>
              Drag the country names onto the map. Correct drops lock in and turn green; incorrect drops add
              to your retry count.
            </p>
            <div className={styles.mapWrapper}>
              <ComposableMap
                projection={region.projection ?? 'geoMercator'}
                projectionConfig={{ scale: mapScale, center: region.center ?? [20, 5] }}
                width={mapSize.width}
                height={mapSize.height}
                style={{ width: '100%', height: 'auto' }}
              >
                <ZoomableGroup
                  minZoom={1}
                  maxZoom={4}
                  zoom={zoom}
                  center={center}
                  translateExtent={[[0, 0], [mapSize.width, mapSize.height]]}
                  onMoveEnd={(position: any) => {
                    if (position?.coordinates) setCenter(position.coordinates);
                    if (position?.zoom) setZoom(position.zoom);
                  }}
                >
                  <Geographies geography={region.geography ?? geoData}>
                    {({ geographies }: { geographies: NamedGeography[] }) => (
                      <>
                        {geographies
                          .filter((geo) => countryByMapName.has(geo.properties.name))
                          .map((geo) => {
                            const country = countryByMapName.get(geo.properties.name);
                            if (!country) return null;
                            const solved = Boolean(placed[country.id]);
                            return (
                              <CountryZone
                                key={country.id}
                                country={country}
                                geography={geo}
                                solved={solved}
                              />
                            );
                          })}
                        {geographies
                          .map((geo) => {
                            const country = countryByMapName.get(geo.properties.name);
                            if (!country || !placed[country.id]) return null;
                            const [lng, lat] = geoCentroid(geo);
                            const nudge =
                              country.code === 'FR'
                                ? [8, 4]
                                : [0, 0];
                            const code = country.code ?? country.label.slice(0, 3).toUpperCase();
                            return (
                              <Marker
                                key={`${country.id}-marker`}
                                coordinates={[lng + nudge[0], lat + nudge[1]]}
                              >
                                <text textAnchor="middle" y={4} className={styles.codeText}>
                                  {code}
                                </text>
                              </Marker>
                            );
                          })}
                      </>
                    )}
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>
              <div className={styles.mapControls}>
                <button
                  type="button"
                  onClick={() => handleZoomChange(0.5)}
                  aria-label="Zoom in"
                >
                  +
                </button>
                <button
                  type="button"
                  onClick={() => handleZoomChange(-0.5)}
                  aria-label="Zoom out"
                >
                  −
                </button>
              </div>
              <div className={styles.mapOverlay}>
                <div className={styles.legendRow}>
                  <span className={styles.legend}>
                    <span className={styles.legendSwatch} style={{ background: '#6FCF97' }} />
                    Correctly placed
                  </span>
                  <span className={styles.legend}>
                    <span className={styles.legendSwatch} style={{ background: '#FFE082' }} />
                    Drop target
                  </span>
                </div>
                {toast && (
                  <div
                    className={[
                      styles.toast,
                      toast.kind === 'success' ? styles.toastSuccess : styles.toastError,
                    ].join(' ')}
                  >
                    {toast.message}
                  </div>
                )}
              </div>
            </div>
          </div>
          <aside className={styles.controls}>
            <h2 className={styles.labelsTitle}>Choose a Region</h2>
            <div className={styles.regionPicker} ref={pickerRef}>
              <button
                type="button"
                className={styles.regionPickerButton}
                aria-haspopup="listbox"
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen((open) => !open)}
              >
                {region.label}
                <span aria-hidden="true" className={styles.regionPickerCaret}>▾</span>
              </button>
              {pickerOpen && (
                <div className={styles.regionPickerMenu} role="listbox">
                  {regions.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="option"
                      aria-selected={r.id === region.id}
                      className={[
                        styles.regionOption,
                        r.id === region.id ? styles.regionOptionActive : '',
                      ].join(' ')}
                      onClick={() => {
                        setRegionId(r.id);
                        setPickerOpen(false);
                      }}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className={styles.subtitle}>
              Drag each country label onto its spot. Locked labels are already correct.
            </p>
            <div className={styles.scores}>
              <div className={styles.scoreCard}>
                <p className={styles.scoreLabel}>Correct</p>
                <p className={styles.scoreValue}>{score.correct}</p>
              </div>
              <div className={styles.scoreCard}>
                <p className={styles.scoreLabel}>Incorrect</p>
                <p className={styles.scoreValue}>{score.wrong}</p>
              </div>
            </div>
            <button
              className={styles.resetButton}
              type="button"
              onClick={() => setResetKey((k) => k + 1)}
            >
              Reset
            </button>
            <div className={styles.labels}>
              {region.countries.map((country) => (
                <DraggableLabel
                  key={country.id}
                  country={country}
                  placed={Boolean(placed[country.id])}
                />
              ))}
              {region.countries.length > 0 && Object.keys(placed).length === region.countries.length && (
                <span className={styles.subtitle}>All countries placed—nice work!</span>
              )}
            </div>
          </aside>
        </div>
        <DragOverlay>
          {activeId ? (
            <DraggableLabel
              country={region.countries.find((c) => c.id === activeId) ?? region.countries[0]}
              placed={false}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
      {showComplete && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal="true">
          <div className={styles.modal}>
            <h3>All countries placed!</h3>
            <p>Great work! Correct: {score.correct} | Incorrect tries: {score.wrong}</p>
            <button type="button" onClick={() => setShowComplete(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorldCountriesQuiz;
